import * as React from 'react';
import { useMemo } from 'react';

import { newProgramMembershipInstance } from 'bundles/program-home/types/ProgramMembershipAdapter';
import EnterpriseProgramContext, {
  useIsProgramLoading,
  useProgram,
} from 'bundles/unified-common/contexts/EnterpriseProgramContext';
import EnterpriseThirdPartyOrganizationContext from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useUserExperienceMemberships } from 'bundles/unified-common/contexts/UserExperienceMembershipsContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';
import mapUserExperienceMembershipToProgramMembershipFragment from 'bundles/unified-description-page-common/providers/org-scoped/utils/mapUserExperienceMembershipToProgramMembershipFragment';

const EnterpriseProgramAndMembershipDataProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const program = useProgram();
  const programId = program?.id;
  const isProgramLoading = useIsProgramLoading();
  const thirdPartyOrganization = useThirdPartyOrganization();

  const { programMemberships, isLoading: isMembershipLoading } = useUserExperienceMemberships();

  const rawMembership = useMemo(
    () => programMemberships?.find((membership) => membership?.node?.program?.id === programId)?.node,
    [programMemberships, programId]
  );

  const programMembership = useMemo(
    () =>
      rawMembership
        ? newProgramMembershipInstance(mapUserExperienceMembershipToProgramMembershipFragment(rawMembership))
        : undefined,
    [rawMembership]
  );

  const contextValue = useMemo(
    () => ({
      program,
      isProgramLoading,
      programMembership,
      isMembershipLoading,
      isProgramMember: Boolean(programMembership?.isProgramMember),
    }),
    [program, isProgramLoading, programMembership, isMembershipLoading]
  );

  return (
    <EnterpriseProgramContext.Provider value={contextValue}>
      <EnterpriseThirdPartyOrganizationContext.Provider value={thirdPartyOrganization}>
        {children}
      </EnterpriseThirdPartyOrganizationContext.Provider>
    </EnterpriseProgramContext.Provider>
  );
};

export default EnterpriseProgramAndMembershipDataProvider;
